/**
 * The staff module's constant values, mirroring `pkg/enums/*` on the API side
 * (docs/OSEBJE.md §5, §7, §11).
 *
 * They are written out here rather than read from the wire because every one of them
 * needs a Slovenian word beside it, and a label assembled from a wire name is a key
 * `yarn check-i18n` cannot see — CLAUDE.md deviation 10, and the reason that check
 * exists.
 */

/** Whether the people named on a shift have been told about it (docs/OSEBJE.md §7). */
export enum StaffShiftState {
    Draft     = 'draft',
    Published = 'published',

    /**
     * A published shift withdrawn. The interval is kept and the reason recorded — a
     * deleted one would take with it the trace of what was promised (§12).
     */
    Cancelled = 'cancelled',
}

/**
 * How far the notice about a CHANGED published shift got (docs/OSEBJE.md §7).
 *
 * The distinction the whole enum exists for: "we sent it" and "she was told" are not
 * the same sentence, and only the second one settles anything when somebody turns up at
 * the wrong hour.
 *
 * In this phase the delivery is IN THE APPLICATION — the person sees the change on
 * their own rota and presses "I understand" — so `Sent` never appears: nothing is sent,
 * because an account carries no e-mail address. It is here because the API's enum has
 * it, and because it becomes true the day a mail sender is built.
 */
export enum StaffNotificationState {
    Pending   = 'pending',
    Sent      = 'sent',
    Delivered = 'delivered',
    Failed    = 'failed',
}

/**
 * HOW a change to a published shift came to be taken in.
 *
 * Kept apart from the delivery state above because they answer different questions and
 * one of the answers is weaker. `Self` is the person's own press on their own rota —
 * their own hand, and the only thing that may write `delivered`. `InPerson` is a
 * supervisor writing down that she told them at the handover, which is the delivery that
 * actually happens when a notice fails or the person carries no channel at all
 * (docs/OBVESCANJE.md §5).
 *
 * `Declined` is on the same field and is NOT one of them: „Ne morem“, answered on the
 * WhatsApp buttons or in the person's own words (§10.1). It settles nothing — a shift
 * somebody cannot work is a hole to fill — so a declined change stays on both lists and
 * says which of the two it is.
 */
export enum StaffAcknowledgement {
    Self     = 'self',
    InPerson = 'in_person',
    Declined = 'declined',
}

/**
 * The legal ground somebody works on. Not only employment: ZEPDSV-B covers every
 * ground, and half a hotel's summer is not in an employment relationship at all
 * (docs/OSEBJE.md §5).
 */
export enum EngagementBasis {
    EmploymentContract  = 'employment_contract',
    AgencyWorker        = 'agency_worker',
    StudentWork         = 'student_work',
    PensionerTemporary  = 'pensioner_temporary',
    Traineeship         = 'traineeship',
    ServiceContract     = 'service_contract',
    CopyrightContract   = 'copyright_contract',
    ShortTermFamilyWork = 'short_term_family_work',
    SelfEmployed        = 'self_employed',
    Other               = 'other',
}

/** A contract of employment's own terms, and nothing else's (docs/OSEBJE.md §5). */
export enum ContractDuration {
    Indefinite = 'indefinite',
    FixedTerm  = 'fixed_term',
}

export enum WorkingTimeType {
    FullTime = 'full_time',
    PartTime = 'part_time',
}

/** What the working-time record is kept FROM. */
export enum TimeRecordBasis {
    Clock    = 'clock',
    Schedule = 'schedule',
    Pattern  = 'pattern',
    Manual   = 'manual',
}

/**
 * Why there is no compulsory record. A managing person lives here and not among the
 * legal grounds above: a director may hold an ordinary contract of employment, and
 * folding the exemption into the ground would make that person unwritable (§5).
 */
export enum TimeRecordExemption {
    ManagingPerson = 'managing_person',
    Other          = 'other',
}

/**
 * Why a requirement source contributed nothing to the coverage answer
 * (docs/OSEBJE.md §11).
 *
 * A nought that was never measured looks exactly like a quiet Tuesday, so every screen
 * showing coverage prints this beside the figures rather than summing it away.
 */
export enum StaffCoverageReason {
    /** Asked and answered. A nought under this one IS a measurement. */
    Contributing = 'contributing',

    /** A soft dependency the house has not bought. */
    NotLicensed = 'not_licensed',

    /** A seam written and not executed yet — ours to close. */
    NotBuilt = 'not_built',

    /** Work that belongs to no department of this house, so it has nowhere honest to go. */
    NoDepartment = 'no_department',

    /**
     * The core source on a department the house has written no coverage rule for.
     *
     * It is the reason this code had to exist: the core is built and licensed, so its
     * standing over the window is "contributing" — and a department it has nothing to say
     * about would otherwise carry a nought wearing that word. Unlike the three above it is
     * the HOUSE's to fix, which is why the screen puts the way to fix it one press away.
     */
    NoRule = 'no_rule',
}

/**
 * How a department's day is costed (docs/OSEBJE.md §11 „Pravila pokritosti“).
 *
 * `None` is a DECISION and not the absence of one: the house has said this department's
 * requirement comes from its own module, so its nought is a measurement. A department with
 * no rule at all is the hole, and it is what `StaffCoverageReason.NoRule` reports.
 */
export enum StaffCoverageRuleKind {
    Desk  = 'desk',
    None  = 'none',
    Fixed = 'fixed',

    /**
     * The three kinds added on 2026-09-08, one per requirement source §11 names and that
     * release finally executed.
     *
     * Every one of them is the desk's own shape in another department's words: the house
     * prices a unit of its work — a sitting, a cover, an event, a head, a call-out — and
     * the module that owns the work counts the units. That second half is what only a
     * property management system holds, and it is the whole of §11's argument.
     */
    Dining      = 'dining',
    Events      = 'events',
    Maintenance = 'maintenance',
}

/** What the coverage grid says that nobody should have to notice by reading it. */
/**
 * The rota rules a week can be standing against (docs/OSEBJE.md §7, „Pravila urnika“).
 *
 * WARNINGS, every one of them — the supervisor may save anyway and the trail records that
 * she did. The one rule that REFUSES is an overlap, and that is not in this enum because
 * it never arrives as a warning: it comes back as a refusal with its own error code
 * (Errors.StaffShiftOverlap), carrying the shift in the way.
 *
 * The sentence beside each code is composed by the server, in the house's language, and
 * drawn verbatim — see IStaffShiftWarning for why the portal does not build it.
 */
export enum StaffShiftWarningCode {
    /** Less than the house's daily rest between two shifts of one person. */
    DailyRest = 'daily_rest',

    /** A seven-day window with no uninterrupted weekly rest anywhere in it. */
    WeeklyRest = 'weekly_rest',

    /** A run of working days longer than the house allows. */
    ConsecutiveDays = 'consecutive_days',

    /** One day rostered past the house's daily ceiling. */
    DailyHours = 'daily_hours',

    /** One week past the person's OWN obligation plus the house's tolerance. */
    WeeklyHours = 'weekly_hours',
}

export enum StaffCoverageWarningCode {
    RecurringShortfall = 'recurring_shortfall',

    /**
     * A department whose requirement nobody has told the system how to compute.
     *
     * The finding this release turns on. A reception rostering sixteen hours read
     * „potrebno 0 min“ and a shortfall of −16 h, and every part of that was arithmetically
     * correct — nothing had said what a desk costs. It is drawn FIRST, above the
     * shortfall, because a shortfall against a requirement of nought is not a finding and
     * would send somebody to fix the wrong thing.
     */
    NoRule = 'no_rule',

    /**
     * Requests for leave nobody has answered inside the window on screen.
     *
     * A warning and never a subtraction: the hours are still counted, because a rota
     * drawn as though every request had been granted would be built on decisions the
     * house has not taken. What it buys is the supervisor being told BEFORE she fills the
     * day in, which is the only moment it is worth anything (docs/OSEBJE.md §10, §11).
     */
    PendingAbsence = 'pending_absence',
}

/**
 * The six cards of „Danes“ (docs/OSEBJE.md §11 „Danes“, docs/OSEBJE-UI.md).
 *
 * The order is the server's, not this enum's: the report answers a LIST and the page
 * draws it in the order it arrives, which is the order the day is worked in. These names
 * exist so that a card can be picked out by hand where one has to be — the strip's icons,
 * the empty sentence — and never to re-sort the list.
 */
export enum StaffTodaySection {
    Coverage    = 'coverage_gaps',
    Stamps      = 'missing_stamps',
    Approvals   = 'waiting_approval',
    Documents   = 'documents_expiring',
    Deliveries  = 'failed_notifications',
    InHouse     = 'in_house',
}

/**
 * What one row of „Danes“ was found by.
 *
 * The kind names the RULE and the screen composes the sentence from it through i18n —
 * unlike a rota warning of §7, whose sentence is written in Go because it carries a
 * person's own figures against the house's own thresholds (CLAUDE.md deviation 24b). A row
 * here carries no rule: a person, a time and an id, and the words around them are a label.
 */
export enum StaffTodayRowKind {
    /** Short even counting the drafts — nobody is coming, and it is red. */
    CoverageGap = 'coverage_gap',

    /** Short on the published rota, and the drafts would close it. Amber: press Publish. */
    CoverageDraftCovers = 'coverage_draft_covers',

    /** A shift that started more than the house's wait ago with nothing stamped. */
    NoArrivalStamp = 'no_arrival_stamp',

    /** A shift that has ended with the person still clocked in. */
    StillClockedIn = 'still_clocked_in',

    /** An arrival from a previous day that was never closed. */
    OpenStamp = 'open_stamp',

    AbsenceRequest = 'absence_request',
    TimeCorrection = 'time_correction',
    ShiftChange    = 'shift_change',

    DocumentExpiring = 'document_expiring',
    DocumentExpired  = 'document_expired',

    DeliveryFailed = 'delivery_failed',

    ClockedIn = 'clocked_in',
    OnAbsence = 'on_absence',
}

/**
 * The payroll program a house hands its closed month to (docs/EVIDENCA-CASA.md
 * § „Payroll Connect“, `pkg/enums/StaffPayrollProvider`).
 *
 * It decides no number. Every provider gets the same rows out of the same snapshot; what
 * it names is the program at the other end, so that a support conversation and a file name
 * say the same word — and so that the day one of them needs a dialect of the file, there
 * is a field to hang it on.
 *
 * `Generic` is the default rather than the first vendor, because a house that has not
 * answered must get the plain file rather than one shaped for somebody else's importer.
 */
export enum StaffPayrollProvider {
    Generic  = 'generic',
    Vasco    = 'vasco',
    Saop     = 'saop',
    Pantheon = 'pantheon',
    Minimax  = 'minimax',
}

/**
 * The file the payroll export is asked for (`pkg/enums/StaffPayrollFormat`).
 *
 * `Xlsx` is a real preference a house may hold and a file this service cannot write: there
 * is no spreadsheet library in it and the approved-dependency list has none. It is in the
 * codebook so that the refusal can name the thing it is refusing — a house that has picked
 * it is told, rather than handed a CSV with the wrong extension.
 */
export enum StaffPayrollFormat {
    Csv  = 'csv',
    Xlsx = 'xlsx',
}

/**
 * How a quantity of time is WRITTEN in the export (`pkg/enums/StaffPayrollHours`).
 *
 * A spelling and never a rounding: 455 minutes is `7,58` or `7:35` and either can be
 * turned back into 455. The monthly SHEET a person signs is always h:mm — this setting
 * governs the file a program imports.
 */
export enum StaffPayrollHours {
    Decimal = 'decimal',
    HHMM    = 'hhmm',
}
